import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  InputAdornment,
  Link,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import OtpInput from 'react-otp-input';
import { request } from '../../Utils/request';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';

const PhoneVerification: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [overrideVerified, setOverrideVerified] = useState(false);

  // Check if phone is already verified
  const isPhoneVerified = !overrideVerified && user?.user?.phone?.isVerified === true;
  const verifiedPhoneNumber = user?.user?.phone?.number;

  const handleUpdateAnotherPhone = () => {
    setOverrideVerified(true);
    setPhoneNumber('');
    setOtp('');
    setStep('phone');
    setError('');
    setSuccess('');
  };


  const handlePhoneSubmit = async () => {
    if (!phoneNumber.trim()) {
      setError('Please enter a valid phone number');
      return;
    }

    if (phoneNumber.length < 10) {
      setError('Phone number must be at least 10 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Call the send OTP API using request utility
      await request('/auth/send-otp', {
        method: 'POST',
        authorization: true,
        body: {
          phoneNumber:'+91'+ phoneNumber
        }
      });

      setSuccess('OTP sent successfully to your phone number');
      setStep('otp');
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async () => {
    if (!otp.trim()) {
      setError('Please enter the OTP');
      return;
    }

    if (otp.length !== 6) {
      setError('OTP must be 6 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Call the verify OTP API using request utility
      await request('/auth/verify-otp', {
        method: 'POST',
        authorization: true,
        body: {
          otp: otp
        }
      });

      setSuccess('Phone number verified successfully!');
      
      // Redirect after successful verification
      setTimeout(() => {
        navigate('/profile');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    setError('');

    try {
      // Call the send OTP API again using request utility
      await request('/auth/resend-otp', {
        method: 'POST',
        authorization: true,
        body: {
          phoneNumber: phoneNumber
        }
      });

      setSuccess('OTP resent successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(e.target.value);
  };

  const handleBackPress = () => {
    if (step === 'otp') {
      // Clear OTP digits one by one (backspace behavior)
      if (otp.length > 0) {
        const newOtp = otp.slice(0, -1);
        setOtp(newOtp);
      } else {
        // If OTP is empty, go back to phone input step
        setStep('phone');
        setError('');
        setSuccess('');
      }
    } else {
      // Navigate away from the page
      navigate(-1);
    }
  };

  // Show already verified screen if phone is verified
  if (isPhoneVerified) {
    return (
      <Box sx={{ bgcolor: '#fff', minHeight: '100vh' }}>
        <TopNavBar 
          title="Phone Verification" 
          canGoBack 
        />
        
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CheckIcon 
              sx={{ 
                fontSize: 80, 
                color: '#4CAF50', 
                mb: 3 
              }} 
            />
            <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
              Phone Already Verified
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Your phone number {verifiedPhoneNumber} is already verified.
            </Typography>
            <Button
              variant="contained"
              onClick={handleUpdateAnotherPhone}
              sx={{ 
                borderRadius: 2,
                px: 4,
                py: 1.5,
                backgroundColor: '#F57C00',
              }}
            >
              Update Another Phone Number
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh' }}>
      <TopNavBar 
        title={step === 'phone' ? 'Verify Phone Number' : 'Verify OTP'} 
        canGoBack 
      />
      

      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Box
        >
          {step === 'phone' ? (
            <>
              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  We'll send you a verification code via SMS
                </Typography>
              </Box>

              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  placeholder="123-456-7890"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  error={!!error && error.includes('phone')}
                  helperText={error.includes('phone') ? error : ''}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />

                {error && !error.includes('phone') && (
                  <Alert severity="error">{error}</Alert>
                )}

                {success && (
                  <Alert severity="success">{success}</Alert>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handlePhoneSubmit}
                  disabled={loading || !phoneNumber.trim()}
                  sx={{ 
                    py: 2,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    backgroundColor: '#F57C00',
                    '&:hover': {
                      backgroundColor: '#E65100',
                    }
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Send OTP'
                  )}
                </Button>
              </Stack>
            </>
          ) : (
            <>
              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                  Enter the 6-digit code sent to your phone number
                </Typography>
              </Box>

              <Stack spacing={4}>
                {/* OTP Input using react-otp-input */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  width: '100%',
                  px: { xs: 1, sm: 2 },
                }}>
                  <Box sx={{
                    width: '100%',
                    maxWidth: { xs: '100%', sm: '420px' },
                    display: 'flex',
                    justifyContent: 'center',
                  }}>
                    <OtpInput
                      value={otp}
                      onChange={setOtp}
                      numInputs={6}
                      renderInput={(props) => (
                        <input
                          {...props}
                          style={{
                            flex: '1 1 0',
                            minWidth: '40px',
                            maxWidth: '55px',
                            width: '100%',
                            height: '50px',
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            border: '2px solid #F57C00',
                            borderRadius: '8px',
                            outline: 'none',
                            backgroundColor: 'white',
                            color: '#333',
                            boxSizing: 'border-box',
                            margin: '0',
                            padding: '0',
                          }}
                        />
                      )}
                      containerStyle={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '6px',
                        flexWrap: 'nowrap',
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                      }}
                      shouldAutoFocus
                    />
                  </Box>
                </Box>

                {error && (
                  <Alert severity="error" sx={{ textAlign: 'center' }}>{error}</Alert>
                )}

                {success && (
                  <Alert severity="success" sx={{ textAlign: 'center' }}>{success}</Alert>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleOtpSubmit}
                  disabled={loading || otp.length !== 6}
                  sx={{ 
                    py: 2,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    backgroundColor: '#F57C00',
                    '&:hover': {
                      backgroundColor: '#E65100',
                    }
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Verify & change password'
                  )}
                </Button>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'inline' }}>
                    Didn't you receive the OTP?{' '}
                  </Typography>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleResendOtp}
                    disabled={loading}
                    sx={{
                      color: '#F57C00',
                      textDecoration: 'none',
                      fontWeight: 'bold',
                      '&:hover': {
                        textDecoration: 'underline',
                      }
                    }}
                  >
                    Resend OTP
                  </Link>
                </Box>
              </Stack>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default PhoneVerification;
